import React, { useState } from "react";
import { FormControl } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { ErrorMessage, useField, useFormikContext } from "formik";
import PropTypes from "prop-types";
import FormikLabel from "./FormikLabel";
import { ErrorStyle } from "./ErrorStyle";
import dayjs from "dayjs";

const FormikDatePicker = ({
  name,
  label,
  width = "100%",
  required = false,
  placeholder = "DD/MM/YYYY",
  disabled = false,
  readOnly = false,
  minDate,
  onChange = () => {},
  views = ["day", "month", "year"],
  format = "DD/MM/YYYY",
  disabledDates = [],
  disablePreviousDates = () => false,
  shouldDisableYear,
  shouldDisableMonth,
  disablePast = false,
  disableFuture = false,
  highlightDates = {}, // e.g. { "2025-08-28": "yellow", "2025-09-05": "orange" }
  actions = ["clear"],
  slotProps: slotPropsProp = {},
  ...props
}) => {
  const { setFieldValue, validateField } = useFormikContext();
  const [open, setOpen] = useState(false);
  const [field] = useField(name);

  const dateFormat = "DD-MM-YYYY";

  return (
    <FormControl sx={{ width }}>
      <FormikLabel text={label} required={required} />
      <DatePicker
        views={views}
        inputFormat={dateFormat}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disabled={disabled}
        readOnly={readOnly}
        value={field.value || null}
        minDate={minDate}
        shouldDisableYear={shouldDisableYear}
        shouldDisableMonth={shouldDisableMonth}
        shouldDisableDate={(date) => {
          const dayjsDate = dayjs(date).startOf("day");
          const today = dayjs().startOf("day");
          return (
            disablePreviousDates(dayjsDate) ||
            (disablePast && dayjsDate.isBefore(today, "day")) ||
            (disableFuture && dayjsDate.isAfter(today, "day")) ||
            disabledDates.includes(dayjsDate.format("YYYY-MM-DD"))
          );
        }}
        onChange={(val) => {
          setFieldValue(field.name, val);
          validateField(field.name);
          onChange(val);
        }}
        slots={{
          // 👇 custom day renderer with highlight colors
          day: (dayProps) => {
            const dateStr = dayjs(dayProps.day).format("YYYY-MM-DD");
            const highlightColor = highlightDates[dateStr];

            return (
              <PickersDay
                {...dayProps}
                sx={{
                  ...(highlightColor && {
                    backgroundColor: highlightColor,
                    color: highlightColor === "yellow" ? "black" : "white",
                    borderRadius: "50px",
                    "&:hover": {
                      backgroundColor:
                        highlightColor === "yellow" ? "gold" : "darkorange",
                    },
                  }),
                }}
              />
            );
          },
        }}
        slotProps={{
          ...slotPropsProp,
          textField: {
            placeholder,
            fullWidth: true,
            InputProps: {
              readOnly: readOnly,
            },
            onKeyDown: (e) => {
              const allowedKeys = [
                "Backspace",
                "Tab",
                "ArrowLeft",
                "ArrowRight",
                "/",
                "Delete",
              ];
              if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            },
            error: false,
            helperText: null,
            sx: {
              "& .MuiOutlinedInput-root": {
                backgroundColor: disabled || readOnly ? "#f5f5f5" : "inherit",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "#c4c4c4",
                },
              },
              "& .MuiInputBase-input": {
                padding: "8px",
                backgroundColor: disabled || readOnly ? "#f5f5f5" : "inherit",
                color: disabled || readOnly ? "#666666" : "inherit",
                cursor: disabled || readOnly ? "not-allowed" : "inherit",
              },
            },
            ...slotPropsProp.textField,
          },
          actionBar: {
            ...slotPropsProp.actionBar,
            actions,
            sx: {
              marginTop: "-66px !important",
              ...slotPropsProp.actionBar?.sx,
            },
          },
        }}
        {...props}
      />
      <div style={ErrorStyle}>
        <ErrorMessage name={name} component="div" />
      </div>
    </FormControl>
  );
};

FormikDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  width: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  minDate: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  views: PropTypes.arrayOf(PropTypes.string),
  format: PropTypes.string,
  disabledDates: PropTypes.arrayOf(PropTypes.string),
  disablePreviousDates: PropTypes.func,
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
  shouldDisableYear: PropTypes.func,
  shouldDisableMonth: PropTypes.func,
  highlightDates: PropTypes.object,
  actions: PropTypes.arrayOf(PropTypes.string),
  slotProps: PropTypes.object,
};

export default FormikDatePicker;
